import React, { useState, useEffect } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { asignacionService } from '../services/matriculaService';
import { AsignacionItem } from '../types/matricula.types';
import { useCompetencias } from '../hooks/useCompetencias';

// ============================================================
// MisCursosPage – Cursos asignados al docente autenticado
// ============================================================

const SpinnerIcon = () => (
  <svg className="animate-spin w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const NIVEL_COLOR: Record<string, string> = {
  INICIAL:    'bg-amber-100 text-amber-700',
  PRIMARIA:   'bg-blue-100 text-blue-700',
  SECUNDARIA: 'bg-purple-100 text-purple-700',
};

const NIVEL_LABEL: Record<string, string> = {
  INICIAL:    'Inicial',
  PRIMARIA:   'Primaria',
  SECUNDARIA: 'Secundaria',
};

// Colores de fondo para las tarjetas según el curso (por índice)
const CARD_ACCENTS = [
  'border-t-blue-500',
  'border-t-purple-500',
  'border-t-green-500',
  'border-t-amber-500',
  'border-t-pink-500',
  'border-t-indigo-500',
  'border-t-teal-500',
  'border-t-orange-500',
];

// ── Modal de competencias (solo lectura) ──────────────────────
const CompetenciasModal: React.FC<{
  cursoId: number;
  cursoNombre: string;
  onClose: () => void;
}> = ({ cursoId, cursoNombre, onClose }) => {
  const { competencias, loading, error } = useCompetencias(cursoId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Cabecera */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-blue-200 text-xs font-medium">Competencias del curso</p>
            <h2 className="text-white font-bold text-base">{cursoNombre}</h2>
          </div>
          <button onClick={onClose}
            className="text-blue-200 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10">
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z"/>
            </svg>
          </button>
        </div>

        {/* Contenido */}
        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-10">
              <svg className="animate-spin w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
              </svg>
            </div>
          ) : error ? (
            <p className="text-center text-red-500 text-sm py-10">{error}</p>
          ) : competencias.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-slate-400 text-sm">Sin competencias registradas para este curso</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-50">
              {competencias.map(c => (
                <li key={c.idCompetencia} className="flex items-start gap-3 px-6 py-3.5 hover:bg-slate-50">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                    {c.orden}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{c.nombre}</p>
                    {c.descripcion && (
                      <p className="text-xs text-slate-400 mt-0.5">{c.descripcion}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-end">
          <button onClick={onClose}
            className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

const MisCursosPage: React.FC = () => {
  const { user } = useAuth();

  const [asignaciones, setAsignaciones] = useState<AsignacionItem[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);
  const [soloActivos, setSoloActivos]   = useState(true);
  const [verCompetencias, setVerCompetencias] = useState<{ cursoId: number; nombre: string } | null>(null);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      setLoading(true); setError(null);
      try {
        const data = await asignacionService.getByDocente(user.idUsuario);
        setAsignaciones(data);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Error al cargar los cursos asignados');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  const visibles = soloActivos
    ? asignaciones.filter(a => a.activo)
    : asignaciones;

  // Agrupar por año escolar para mostrar secciones separadas
  const porAnio = visibles.reduce<Record<number, AsignacionItem[]>>((acc, a) => {
    if (!acc[a.anio]) acc[a.anio] = [];
    acc[a.anio].push(a);
    return acc;
  }, {});
  const anios = Object.keys(porAnio).map(Number).sort((a, b) => b - a);

  const totalActivos   = asignaciones.filter(a => a.activo).length;
  const totalInactivos = asignaciones.filter(a => !a.activo).length;

  return (
    <div className="space-y-6">

      {/* Cabecera de bienvenida */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-2xl p-6 text-white shadow-lg">
        <p className="text-blue-200 text-sm font-medium mb-1">Docente</p>
        <h2 className="text-2xl font-bold">{user?.nombres} {user?.apellidos}</h2>
        <p className="text-blue-200 text-sm mt-1">
          {totalActivos} curso{totalActivos !== 1 ? 's' : ''} asignado{totalActivos !== 1 ? 's' : ''} este año
        </p>
      </div>

      {/* Resumen */}
      {!loading && !error && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center mb-3">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342"/>
              </svg>
            </div>
            <p className="text-2xl font-bold text-slate-800">{totalActivos}</p>
            <p className="text-slate-400 text-sm">Cursos activos</p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center mb-3">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636"/>
              </svg>
            </div>
            <p className="text-2xl font-bold text-slate-800">{totalInactivos}</p>
            <p className="text-slate-400 text-sm">Cursos inactivos</p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-green-100 text-green-600 flex items-center justify-center mb-3">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
              </svg>
            </div>
            <p className="text-2xl font-bold text-slate-800">{anios[0] ?? '—'}</p>
            <p className="text-slate-400 text-sm">Año escolar actual</p>
          </div>
        </div>
      )}

      {/* Filtro */}
      {!loading && !error && asignaciones.length > 0 && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSoloActivos(true)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors
              ${soloActivos ? 'bg-blue-700 text-white shadow-sm' : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'}`}
          >
            Activos ({totalActivos})
          </button>
          <button
            onClick={() => setSoloActivos(false)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors
              ${!soloActivos ? 'bg-blue-700 text-white shadow-sm' : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'}`}
          >
            Todos ({asignaciones.length})
          </button>
        </div>
      )}

      {/* Contenido */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <SpinnerIcon />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      ) : asignaciones.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-16 text-center">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
            className="w-12 h-12 text-slate-200 mx-auto mb-3">
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342"/>
          </svg>
          <p className="text-slate-400 text-sm font-medium">Sin cursos asignados</p>
          <p className="text-slate-300 text-xs mt-1">Contacta al administrador para que te asigne cursos</p>
        </div>
      ) : visibles.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-12 text-center">
          <p className="text-slate-400 text-sm">No hay cursos activos en este momento</p>
        </div>
      ) : (
        /* Agrupado por año escolar */
        anios.map(anio => (
          <div key={anio} className="space-y-3">
            {/* Separador de año */}
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                Año Escolar {anio}
              </span>
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs text-slate-400">{porAnio[anio].length} curso{porAnio[anio].length !== 1 ? 's' : ''}</span>
            </div>

            {/* Tarjetas */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {porAnio[anio].map((a, idx) => (
                <div
                  key={a.idAsignacion}
                  className={`bg-white rounded-2xl border border-slate-100 shadow-sm
                    border-t-4 ${CARD_ACCENTS[idx % CARD_ACCENTS.length]}
                    ${!a.activo ? 'opacity-60' : ''}
                    transition-shadow hover:shadow-md`}
                >
                  <div className="p-5">
                    {/* Nombre del curso */}
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <h3 className="font-bold text-slate-800 text-base leading-tight">
                        {a.cursoNombre}
                      </h3>
                      {!a.activo && (
                        <span className="flex-shrink-0 text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                          Inactivo
                        </span>
                      )}
                    </div>

                    {/* Detalles */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-slate-400 flex-shrink-0">
                          <path d="M10.394 2.08a1 1 0 0 0-.788 0l-7 3a1 1 0 0 0 0 1.84L5.25 8.051a.999.999 0 0 1 .356-.257l4-1.714a1 1 0 1 1 .788 1.838L7.667 9.088l1.94.831a1 1 0 0 0 .787 0l7-3a1 1 0 0 0 0-1.838l-7-3ZM3.31 9.397 5 10.12v4.102a8.969 8.969 0 0 0-1.05-.174 1 1 0 0 1-.89-.89 11.115 11.115 0 0 1 .25-3.762ZM9.3 16.573A9.026 9.026 0 0 0 10 16.25a9.026 9.026 0 0 0 .7.323l-.7.7-.7-.7ZM11 13.25v-2.093l4-1.714v3.48a2.99 2.99 0 0 1-1.5 2.59V15.5a.75.75 0 0 0-1.5 0v.513a2.99 2.99 0 0 1-1 0V15.5a.75.75 0 0 0-1.5 0v.267a2.993 2.993 0 0 1-.5-1.517Z"/>
                        </svg>
                        <span>
                          {a.gradoNombre} — Sección <strong>{a.seccionNombre}</strong>
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full
                          ${NIVEL_COLOR[a.gradoNivel] ?? 'bg-slate-100 text-slate-600'}`}>
                          {NIVEL_LABEL[a.gradoNivel] ?? a.gradoNivel}
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Pie de tarjeta: ver competencias */}
                  <div className="border-t border-slate-100 px-5 py-2.5">
                    <button
                      onClick={() => setVerCompetencias({ cursoId: a.cursoId, nombre: a.cursoNombre })}
                      className="text-xs font-semibold text-purple-600 hover:text-purple-800 flex items-center gap-1 transition-colors"
                    >
                      <svg viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5">
                        <path d="M2 3.5a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5ZM2 8a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11A.5.5 0 0 1 2 8Zm0 4.5a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5Z"/>
                      </svg>
                      Ver competencias
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {/* Modal de competencias */}
      {verCompetencias && (
        <CompetenciasModal
          cursoId={verCompetencias.cursoId}
          cursoNombre={verCompetencias.nombre}
          onClose={() => setVerCompetencias(null)}
        />
      )}
    </div>
  );
};

export default MisCursosPage;
